import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { openContextMenu, closeContextMenu } from "../../../Redux/actions/ui.js";
import ModelProperties from "./modules/ModelProperties/ModelProperties.js";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import Inventum from "../../../../engine/Inventum";
import { selectPermissionCapabilities } from "../../../utils/permissions.js";
import { findSortableIndex, getModelSortableId, getSubGroupSortableId } from "./sortableIds.js";

const DragHandle = ({ listeners, attributes }) => (
  <div className="DragHandle" {...listeners} {...attributes}>
    <i className="material-icons notranslate">drag_indicator</i>
  </div>
);

class ModelTitle extends React.Component {
  constructor(props) {
    super(props);
    this.state = { name: props.name };
    this.handleChange = this.handleChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
  }

  handleChange(e) {
    this.setState({ name: e.target.value });
  }

  handleSave() {
    Inventum.models.setModelName(this.props.id, this.state.name);
    this.props.cancelEditing();
  }

  componentDidUpdate(prevProps) {
    if (this.props.editing === true && prevProps.editing === false) {
      this.inputBox.focus();
      this.inputBox.select();
    }
  }

  render() {
    let handleKey = (e) => {
      if (e.key === "Enter") {
        this.handleSave();
      }
    };

    if (this.props.editing) {
      return (
        <div
          className="ModelTitle editing"
          onClick={(e) => {
            e.stopPropagation();
          }}>
          <input
            onKeyDown={handleKey}
            ref={(input) => {
              this.inputBox = input;
            }}
            value={this.state.name}
            onChange={this.handleChange}
          />
          <div className="PanelTitleEditButton" onClick={this.handleSave}>
            Save
          </div>
          <div
            className="PanelTitleEditButton"
            onClick={() => {
              this.setState({ name: this.props.name });
              this.props.cancelEditing();
            }}>
            Cancel
          </div>
        </div>
      );
    }

    let needsReviewStyle = { fontSize: "16px", paddingRight: "5px", color: "#ffd24d" };
    return (
      <div className="ModelTitle" title={this.props.name}>
        {this.props.needsReview && this.props.editMode ? (
          <i className="material-icons notranslate" style={needsReviewStyle} title="New Model">
            new_releases
          </i>
        ) : null}
        {this.props.name}
      </div>
    );
  }
}

function ModelColorPicker(props) {
  const handleClick = (e) => {
    e.stopPropagation();
    props.onOpenColorPicker();
  };

  return (
    <div className={`ModelColorPicker${props.active ? " is-open" : ""}`} onClick={handleClick} title="Open colour picker">
      <div className="ColorPreview" style={{ backgroundColor: props.color }} />
    </div>
  );
}

// function ModelWireframe(props) {
//   let click = () => {
//     Inventum.models.toggleWireframe(props.id);
//   };
//   return (
//     <div className="ModelWireframeToggle" onClick={click}>
//       <i className="material-icons model-controls">{props.wireframe ? "check_box" : "check_box_outline_blank"}</i>
//     </div>
//   );
// }

// function ModelOpacity(props) {
//   return (
//     <div className="ModelWireframeToggle" onClick={props.handleClick}>
//       <i className="material-icons model-controls">opacity</i>
//     </div>
//   );
// }

function ModelZoomToggle(props) {
  const handleClick = (e) => {
    e.stopPropagation();
    Inventum.camera.zoomToObject(props.id);
  };
  return (
    <div className="ModelZoomToggle" onClick={handleClick} aria-label="zoom" style={{ padding: "0px" }}>
      <i className="material-icons model-controls">zoom_in_map</i>
    </div>
  );
}

// function ModelLabelToggle(props) {
//   let click = () => {
//     Inventum.models.toggleLabels(props.id);
//   };
//   var style = props.visible ? {} : { color: "#222222" };

//   if (!props.hasLabels) {
//     return (
//       <div className="ModelLabelToggle">
//         <i className="material-icons model-controls"></i>
//       </div>
//     );
//   }

//   return (
//     <div className="ModelLabelToggle" onClick={click}>
//       <i className="material-icons model-controls" style={style}>
//         label
//       </i>
//     </div>
//   );
// }

function ModelVisibilityToggle(props) {
  let click = (e) => {
    e.stopPropagation();
    if (props.id !== undefined) {
      Inventum.models.toggleVisibility(props.id);
    }
  };
  var style = props.visible ? {} : { color: "#222222" };
  return (
    <div className="ModelVisibilityToggle" onClick={click}>
      <i className="material-icons model-controls" style={style}>
        {props.visible ? "check_box" : "check_box_outline_blank"}
      </i>
    </div>
  );
}

function ModelEntryContextMenu(props) {
  let handleRename = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    props.closeMenu();
    props.setEditing();
  };

  let handleDelete = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    if (window.confirm("Delete Model?")) {
      Inventum.models.removeModel(props.model.id);
    }
    props.closeMenu();
  };

  let handleCopyName = (e) => {
    e.stopPropagation();
    if (!props.siteAdmin) {
      return;
    }
    navigator.clipboard
      .writeText(props.model.name)
      .then(() => {
        console.log("Model name copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy model name: ", err);
      });
    props.closeMenu();
  };

  let handlePasteName = (e) => {
    e.stopPropagation();
    if (!props.siteAdmin) {
      return;
    }
    navigator.clipboard
      .readText()
      .then((text) => {
        Inventum.models.setModelName(props.model.id, text);
        console.log("Model name pasted from clipboard");
      })
      .catch((err) => {
        console.error("Failed to paste model name: ", err);
      });
    props.closeMenu();
  };

  let handleVisibleStart = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    props.closeMenu();
    Inventum.models.toggleVisibleStart(props.model.id);
  };

  let handleCastShadow = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    props.closeMenu();
    Inventum.models.toggleCastShadow(props.model.id);
  };

  let handleReceiveShadow = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    props.closeMenu();
    Inventum.models.toggleReceiveShadow(props.model.id);
  };

  let handleCut = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    props.closeMenu();
    Inventum.models.cutModel(props.model.id);
  };

  let handleCopyMaterial = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    props.closeMenu();
    Inventum.models.copyMaterial(props.model.id);
  };

  let handlePasteMaterial = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    Inventum.models.pasteMaterial(props.model.id);
    props.closeMenu();
    Inventum.models.setTexture(props.model.id, Inventum.models.getMaterial(props.model.id).mapPath);
    Inventum.models.setMaterial(props.model.id, Inventum.models.getMaterial(props.model.id));
    Inventum.models.getModelData();
  };

  let handleCopySceneStates = (e) => {
    e.stopPropagation();
    Inventum.models.copySceneStates(props.model.id);
    props.closeMenu();
  };

  let handlePasteSceneStates = (e) => {
    e.stopPropagation();
    Inventum.models.pasteSceneStates(props.model.id);
    props.closeMenu();
  };

  const CopyMaterialAndSceneStates = (e) => {
    handleCopyMaterial(e);
    handleCopySceneStates(e);
  };

  const PasteMaterialAndSceneStates = (e) => {
    handlePasteMaterial(e);
    handlePasteSceneStates(e);
  };

  let handleNeedsReview = (e) => {
    e.stopPropagation();
    Inventum.models.toggleNeedsReview(props.model.id);
    props.closeMenu();
  };

  let handleTransformModel = (e) => {
    e.stopPropagation();
    Inventum.models.transformModel(props.model.id);
    props.closeMenu();
  };

  if (props.pageMode !== "3D") {
    return null;
  }
  if (!props.siteAdmin || !props.editMode) {
    return <div className="SideBarPanelContextMenu" />;
  }
  return (
    <div className="SideBarPanelContextMenu">
      <div className="SideBarContextMenuEntry" onClick={handleRename}>
        Rename Model
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleCopyName}>
        Copy Model Name
      </div>
      <div className="SideBarContextMenuEntry" onClick={handlePasteName}>
        Paste Model Name
      </div>
      <div className="SideBarContextMenuEntry MenuEntryWarning" onClick={handleDelete}>
        Delete Model
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleCut}>
        Cut Model
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleCopyMaterial}>
        Copy Material
      </div>
      <div className="SideBarContextMenuEntry" onClick={handlePasteMaterial}>
        Paste Material
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleCopySceneStates}>
        Copy Scene States
      </div>
      <div className="SideBarContextMenuEntry" onClick={handlePasteSceneStates}>
        Paste Scene States
      </div>
      <div className="SideBarContextMenuEntry" onClick={CopyMaterialAndSceneStates}>
        Copy Material And Scene States
      </div>
      <div className="SideBarContextMenuEntry" onClick={PasteMaterialAndSceneStates}>
        Paste Material And Scene States
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleTransformModel}>
        <input readOnly name="Transform Active" type="checkbox" checked={props.model.transformActive} />
        Transform Model
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleVisibleStart}>
        <input readOnly name="Visible Start" type="checkbox" checked={props.model.visibleStart} />
        Visible Start
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleCastShadow}>
        <input readOnly name="Cast Shadow" type="checkbox" checked={props.model.castShadow} />
        Cast Shadow
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleReceiveShadow}>
        <input readOnly name="Receive Shadow" type="checkbox" checked={props.model.receiveShadow} />
        Receive Shadow
      </div>
      <div className="SideBarContextMenuEntry" onClick={handleNeedsReview}>
        <input readOnly name="Needs Review" type="checkbox" checked={props.model.needsReview} />
        Needs Review
      </div>
    </div>
  );
}

class ModelEntry extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      modelPropertiesVisible: false,
      editingName: false,
      requestedColorInput: null,
      colorInputRequest: 0,
    };
    this.toggleModelProperties = this.toggleModelProperties.bind(this);
    this.openModelPropertiesColorPicker = this.openModelPropertiesColorPicker.bind(this);
    this.handleContext = this.handleContext.bind(this);
    this.handleHeaderClick = this.handleHeaderClick.bind(this);
  }

  handleContext(force) {
    if (this.props.pageMode === "3D" && !this.props.editMode) {
      return;
    }
    if (typeof force === "boolean") {
      if (force) {
        this.props.openContextMenu(this.props.model.id);
      } else {
        this.props.closeContextMenu();
      }
      return;
    }
    if (this.props.model.id === this.props.activeContextMenu) {
      this.props.closeContextMenu();
    } else {
      this.props.openContextMenu(this.props.model.id);
    }
  }

  toggleModelProperties() {
    this.setState({ modelPropertiesVisible: !this.state.modelPropertiesVisible }, () => {
      if (this.state.modelPropertiesVisible) {
        Inventum.models.getModelData();
      }
    });
  }

  openModelPropertiesColorPicker() {
    this.handleContext(false);
    this.setState((state) => ({
      modelPropertiesVisible: true,
      requestedColorInput: "color",
      colorInputRequest: state.colorInputRequest + 1,
    }), () => {
      Inventum.models.getModelData();
    });
  }

  handleHeaderClick() {
    this.handleContext(false);
    this.toggleModelProperties();
  }

  render() {
    const { model, editMode, attributes, listeners, setNodeRef, transform, transition, isDragging } = this.props;

    let contextMenuOpen = model.id === this.props.activeContextMenu;

    if (!model.isLoaded) {
      return (
        <div style={{ justifyContent: "flex-start" }} className={"ModelEntry"}>
          {editMode && <DragHandle listeners={listeners} attributes={attributes} />}
          <span style={{ fontSize: "12px", paddingLeft: "40px", minWidth: "155px" }}>{model.name}</span>
          <div
            onClick={() => {
              Inventum.models.fetchModel(model.id);
            }}
            className="LoadModelButton"
            style={{ fontSize: "12px" }}>
            Click to Load
          </div>
        </div>
      );
    }

    const handleRightClickHeader = (e) => {
      const urlParams = new URLSearchParams(window.location.search);
      const myParam = urlParams.get("options");
      if (myParam === "disableSidebarContext") {
        return;
      }
      e.preventDefault();
      this.handleContext();
    };

    const style = {
      transform: CSS.Transform.toString(transform),
      transition: isDragging ? "none" : transition,
      zIndex: isDragging ? 1000 : undefined,
      position: isDragging ? "relative" : undefined,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={"ModelEntry" + (contextMenuOpen ? " active" : "") + (isDragging ? " dragging" : "")}
        onClick={this.handleHeaderClick}>
        <div className="ModelEntryHeaderWrapper" onContextMenu={handleRightClickHeader}>
          {editMode && <DragHandle listeners={listeners} attributes={attributes} />}
          {contextMenuOpen ? (
            <ModelEntryContextMenu
              model={model}
              pageMode={this.props.pageMode}
              siteAdmin={this.props.siteAdmin}
              editMode={this.props.editMode}
              closeMenu={() => {
                this.handleContext(false);
              }}
              setEditing={() => {
                this.setState({ editingName: true });
              }}
            />
          ) : null}
          <ModelVisibilityToggle id={model.id} visible={model.visible} />
          <div>
            <i className="material-icons notranslate">
              {this.state.modelPropertiesVisible ? "keyboard_arrow_down" : "keyboard_arrow_right"}
            </i>
          </div>
          <ModelTitle
            editMode={this.props.editMode}
            name={model.name}
            id={model.id}
            needsReview={model.needsReview}
            editing={this.state.editingName}
            cancelEditing={() => {
              this.setState({ editingName: false });
            }}
          />
          {!this.state.editingName ? (
            <ModelColorPicker
              id={model.id}
              color={model.color}
              active={this.state.modelPropertiesVisible && this.state.requestedColorInput === "color"}
              onOpenColorPicker={this.openModelPropertiesColorPicker}
            />
          ) : null}
          {/* {!this.state.editingName ? <ModelOpacity id={model.id} /> : null} */}
          {!this.state.editingName ? <ModelZoomToggle id={model.id} /> : null}
        </div>
        {this.state.modelPropertiesVisible && !isDragging ? (
          <ModelProperties
            activeProjectID={this.props.activeProjectID}
            modelProps={model}
            id={model.id}
            editMode={this.props.editMode}
            siteAdmin={this.props.siteAdmin}
            requestedColorInput={this.state.requestedColorInput}
            colorInputRequest={this.state.colorInputRequest}
          />
        ) : null}
      </div>
    );
  }
}

function SortableModelEntry(props) {
  const sortableId = props.sortableId ?? getModelSortableId(props.model, props.collectionId);
  const dndId = sortableId ?? `sidebar-model-missing-stable-id-${props.collectionId ?? "unscoped"}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dndId, disabled: sortableId === null });

  return (
    <ModelEntry
      {...props}
      setNodeRef={setNodeRef}
      attributes={attributes}
      listeners={listeners}
      transform={transform}
      transition={transition}
      isDragging={isDragging}
    />
  );
}

let mapStateToProps = (state) => {
  const caps = selectPermissionCapabilities(state);

  return {
    // Legacy prop name retained for compatibility; capability-based source.
    siteAdmin: caps.canAccessEditorUI,
    editMode: state.ui.editMode,
    pageMode: state.sidebarUI.mode,
    activeContextMenu: state.ui.activeContextMenu,
    // Prefer ui.activeProject (set on viewer load), with activePage.projectID as fallback.
    // Some object-edit flows (e.g. DTM texture upload) rely on this being populated.
    activeProjectID: state.ui.activeProject || state.activePage.projectID,
    reviewMode: state.ui.reviewMode,
  };
};

let mapDispatchToProps = (dispatch) => ({
  openContextMenu: (panelID) => {
    dispatch(openContextMenu(panelID));
  },
  closeContextMenu: () => {
    dispatch(closeContextMenu());
  },
});

let ModelEntryWrapper = connect(mapStateToProps, mapDispatchToProps)(SortableModelEntry);

// Sub-group Components

class SubGroupTitle extends React.Component {
  constructor(props) {
    super(props);
    this.state = { name: props.name };
    this.handleChange = this.handleChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
  }

  handleChange(e) {
    this.setState({ name: e.target.value });
  }

  handleSave() {
    Inventum.models.setSubGroupName(this.props.id, this.state.name);
    this.props.cancelEditing();
  }

  componentDidUpdate(prevProps) {
    if (this.props.editing === true && prevProps.editing === false) {
      this.inputBox.focus();
      this.inputBox.select();
    }
  }

  render() {
    let handleKey = (e) => {
      if (e.key === "Enter") {
        this.handleSave();
      }
    };

    if (this.props.editing) {
      return (
        <div
          className="SubGroupTitle editing"
          onClick={(e) => {
            e.stopPropagation();
          }}>
          <input
            onKeyDown={handleKey}
            ref={(input) => {
              this.inputBox = input;
            }}
            value={this.state.name}
            onChange={this.handleChange}
          />
          <div className="PanelTitleEditButton" onClick={this.handleSave}>
            Save
          </div>
          <div
            className="PanelTitleEditButton"
            onClick={() => {
              this.setState({ name: this.props.name });
              this.props.cancelEditing();
            }}>
            Cancel
          </div>
        </div>
      );
    }

    return (
      <div className="SubGroupTitle" title={this.props.name}>
        {this.props.name}
      </div>
    );
  }
}

function SubGroupVisibilityToggle(props) {
  let click = (e) => {
    e.stopPropagation();
    if (props.id !== undefined) {
      Inventum.models.toggleSubGroupVisibility(props.id);
    }
  };

  let iconStyle = {};
  if (props.visibility === "none") {
    iconStyle = { color: "#222222" };
  } else if (props.visibility === "mixed") {
    iconStyle = { color: "#888888" };
  }

  let icon = "check_box";
  if (props.visibility === "none") {
    icon = "check_box_outline_blank";
  } else if (props.visibility === "mixed") {
    icon = "indeterminate_check_box";
  }

  return (
    <div className="SubGroupVisibilityToggle" onClick={click}>
      <i className="material-icons model-controls" style={iconStyle}>
        {icon}
      </i>
    </div>
  );
}

function SubGroupContextMenu(props) {
  let handleRename = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    props.closeMenu();
    props.setEditing();
  };

  let handleDelete = (e) => {
    if (!props.siteAdmin) {
      return;
    }
    e.stopPropagation();
    if (window.confirm("Delete Sub-group?")) {
      Inventum.models.deleteSubGroup(props.subGroupId);
    }
    props.closeMenu();
  };

  let handleAdd = (e) => {
    if (!props.canAddPasteModels) {
      return;
    }
    props.openModal("InventumAssetManager");
    props.setModalOptions({ type: "mesh", referenceID: props.subGroupId, isSubGroup: true });
    e.stopPropagation();
    props.closeMenu();
  };

  let handlePaste = (e) => {
    if (!props.canAddPasteModels) {
      return;
    }
    e.stopPropagation();
    props.closeMenu();
    Inventum.models.pasteModelToSubGroup(props.subGroupId);
  };

  const handleDisabledEntryClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addPasteClassName = `SideBarContextMenuEntry${props.canAddPasteModels ? "" : " MenuEntryDisabled"}`;

  if (props.pageMode !== "3D") {
    return null;
  }
  if (!props.siteAdmin || !props.editMode) {
    return <div className="SubGroupContextMenu" />;
  }

  return (
    <div className="SubGroupContextMenu">
      <div className="SideBarContextMenuEntry" onClick={handleRename}>
        Rename Sub-group
      </div>
      <div className="SideBarContextMenuEntry MenuEntryWarning" onClick={handleDelete}>
        Delete Sub-group
      </div>
      <div
        className={addPasteClassName}
        onClick={props.canAddPasteModels ? handleAdd : handleDisabledEntryClick}
        aria-disabled={!props.canAddPasteModels}
        title={props.canAddPasteModels ? undefined : "Site admins only"}>
        Add Model
      </div>
      <div
        className={addPasteClassName}
        onClick={props.canAddPasteModels ? handlePaste : handleDisabledEntryClick}
        aria-disabled={!props.canAddPasteModels}
        title={props.canAddPasteModels ? undefined : "Site admins only"}>
        Paste Model
      </div>
    </div>
  );
}

class SubGroupEntry extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      editingName: false,
    };
    this.toggleOpen = this.toggleOpen.bind(this);
    this.handleContext = this.handleContext.bind(this);
  }

  handleContext(force) {
    if (this.props.pageMode === "3D" && !this.props.editMode) {
      return;
    }
    if (typeof force === "boolean") {
      if (force) {
        this.props.openContextMenu("subgroup-" + this.props.subGroup.id);
      } else {
        this.props.closeContextMenu();
      }
      return;
    }
    if ("subgroup-" + this.props.subGroup.id === this.props.activeContextMenu) {
      this.props.closeContextMenu();
    } else {
      this.props.openContextMenu("subgroup-" + this.props.subGroup.id);
    }
  }

  toggleOpen() {
    this.handleContext(false);
    this.setState({ isOpen: !this.state.isOpen });
  }

  render() {
    const { subGroup, editMode, attributes, listeners, setNodeRef, transform, transition, isDragging } = this.props;
    const contextMenuOpen = "subgroup-" + subGroup.id === this.props.activeContextMenu;

    const style = {
      transform: CSS.Transform.toString(transform),
      transition: isDragging ? "none" : transition,
      zIndex: isDragging ? 1000 : undefined,
      position: isDragging ? "relative" : undefined,
    };

    const handleRightClick = (e) => {
      const urlParams = new URLSearchParams(window.location.search);
      const myParam = urlParams.get("options");
      if (myParam === "disableSidebarContext") {
        return;
      }
      e.preventDefault();
      this.handleContext();
    };

    const handleModelSort = (data) => {
      Inventum.models.sortModelsInSubGroup(subGroup.id, data);
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={"SubGroupEntry" + (contextMenuOpen ? " active" : "") + (isDragging ? " dragging" : "")}>
        <div className="SubGroupEntryHeader" onClick={this.toggleOpen} onContextMenu={handleRightClick}>
          {editMode && <DragHandle listeners={listeners} attributes={attributes} />}
          {contextMenuOpen ? (
            <SubGroupContextMenu
              subGroupId={subGroup.id}
              pageMode={this.props.pageMode}
              siteAdmin={this.props.siteAdmin}
              editMode={this.props.editMode}
              canAddPasteModels={this.props.canAddPasteModels}
              openModal={this.props.openModal}
              setModalOptions={this.props.setModalOptions}
              closeMenu={() => {
                this.handleContext(false);
              }}
              setEditing={() => {
                this.setState({ editingName: true });
              }}
            />
          ) : null}
          <SubGroupVisibilityToggle id={subGroup.id} visibility={subGroup.visibility} />
          <div>
            <i className="material-icons notranslate">{this.state.isOpen ? "keyboard_arrow_down" : "keyboard_arrow_right"}</i>
          </div>
          <SubGroupTitle
            name={subGroup.name}
            id={subGroup.id}
            editing={this.state.editingName}
            cancelEditing={() => {
              this.setState({ editingName: false });
            }}
          />
        </div>
        {this.state.isOpen && !isDragging && subGroup.items && subGroup.items.length > 0 ? (
          <div className="SubGroupBody">
            <ModelCollection collectionId={subGroup.id} onSortEnd={handleModelSort} items={subGroup.items} reviewMode={this.props.reviewMode} />
          </div>
        ) : null}
      </div>
    );
  }
}

function SortableSubGroupEntry(props) {
  const sortableId = props.sortableId ?? getSubGroupSortableId(props.subGroup, props.parentId);
  const dndId = sortableId ?? `sidebar-subgroup-missing-stable-id-${props.parentId ?? "unscoped"}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dndId, disabled: sortableId === null });

  return (
    <SubGroupEntry
      {...props}
      setNodeRef={setNodeRef}
      attributes={attributes}
      listeners={listeners}
      transform={transform}
      transition={transition}
      isDragging={isDragging}
    />
  );
}

let subGroupMapStateToProps = (state) => {
  const caps = selectPermissionCapabilities(state);

  return {
    // Legacy prop name retained for compatibility; capability-based source.
    siteAdmin: caps.canAccessEditorUI,
    canAddPasteModels: caps.canAddPasteModels,
    editMode: state.ui.editMode,
    pageMode: state.sidebarUI.mode,
    activeContextMenu: state.ui.activeContextMenu,
    reviewMode: state.ui.reviewMode,
  };
};

let subGroupMapDispatchToProps = (dispatch) => ({
  openContextMenu: (id) => {
    dispatch(openContextMenu(id));
  },
  closeContextMenu: () => {
    dispatch(closeContextMenu());
  },
});

let SubGroupEntryWrapper = connect(subGroupMapStateToProps, subGroupMapDispatchToProps)(SortableSubGroupEntry);

function SubGroupCollection(props) {
  const [items, setItems] = useState(props.subGroups);
  const parentId = props.parentId ?? "unscoped";
  const getSortableId = useCallback((item) => getSubGroupSortableId(item, parentId), [parentId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setItems((items) => {
      const oldIndex = findSortableIndex(items, active.id, getSortableId);
      const newIndex = findSortableIndex(items, over.id, getSortableId);

      if (oldIndex === -1 || newIndex === -1) {
        return items;
      }

      const newArray = arrayMove(items, oldIndex, newIndex);

      if (props.onSortEnd) {
        props.onSortEnd({
          oldIndex,
          newIndex,
        });
      }

      return newArray;
    });
  }

  useEffect(() => {
    setItems(props.subGroups);
  }, [props.subGroups]);

  if (!items || items.length === 0) {
    return null;
  }

  if (items.length <= 1) {
    return (
      <div className="SubGroupCollection">
        {items.map((subGroup) => {
          const sortableId = getSortableId(subGroup);
          return (
            <SubGroupEntryWrapper
              key={sortableId ?? `sidebar-subgroup-missing-${subGroup.id ?? subGroup.shortID ?? subGroup.name}`}
              sortableId={sortableId}
              parentId={parentId}
              subGroup={subGroup}
              openModal={props.openModal}
              setModalOptions={props.setModalOptions}
            />
          );
        })}
      </div>
    );
  }

  const sortableItems = items.map(getSortableId).filter((item) => item !== null);

  return (
    <div className="SubGroupCollection">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          {items.map((subGroup) => {
            const sortableId = getSortableId(subGroup);
            return (
              <SubGroupEntryWrapper
                key={sortableId ?? `sidebar-subgroup-missing-${subGroup.id ?? subGroup.shortID ?? subGroup.name}`}
                sortableId={sortableId}
                parentId={parentId}
                subGroup={subGroup}
                openModal={props.openModal}
                setModalOptions={props.setModalOptions}
              />
            );
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
}

function ModelCollection(props) {
  const [items, setItems] = useState(props.items);
  const collectionId = props.collectionId ?? "unscoped";
  const getSortableId = useCallback((item) => getModelSortableId(item, collectionId), [collectionId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setItems((items) => {
      const oldIndex = findSortableIndex(items, active.id, getSortableId);
      const newIndex = findSortableIndex(items, over.id, getSortableId);

      if (oldIndex === -1 || newIndex === -1) {
        return items;
      }

      const newArray = arrayMove(items, oldIndex, newIndex);

      // Call the original onSortEnd from props
      if (props.onSortEnd) {
        props.onSortEnd({
          oldIndex,
          newIndex,
        });
      }

      return newArray;
    });
  }

  // Handle updates from props
  useEffect(() => {
    setItems(props.items);
  }, [props.items]);

  // If only one item, disable sorting
  if (items.length <= 1) {
    return (
      <div className="ModelCollection">
        {items.map((model) => {
          const sortableId = getSortableId(model);
          return (
            <ModelEntryWrapper
              key={sortableId ?? `sidebar-model-missing-${model.id ?? model.shortID ?? model.name}`}
              sortableId={sortableId}
              collectionId={collectionId}
              model={model}
            />
          );
        })}
      </div>
    );
  }

  const sortableItems = items.map(getSortableId).filter((item) => item !== null);

  return (
    <div className="ModelCollection">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          {items.map((model) => {
            const sortableId = getSortableId(model);
            return (
              <ModelEntryWrapper
                key={sortableId ?? `sidebar-model-missing-${model.id ?? model.shortID ?? model.name}`}
                sortableId={sortableId}
                collectionId={collectionId}
                model={model}
              />
            );
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
}

export default ModelCollection;
export { SubGroupCollection };
